import { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Link, useNavigate } from 'react-router-dom'
import { SIDEBAR_OPENED_WIDTH } from '@/components/layout/MainSideBar'
import { AUTH_ERROR_CODE, HEADER_HEIGHT, HTTP_BAD_REQUEST, SHIPPER_NO_MENU_MESSAGE } from '@/utils/consts'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import MConfirm from '@/utils/modal/MConfirm'
import MAlert from '@/utils/modal/MAlert'
import { AuthUtils } from '@/utils/AuthUtils'
import RemixIcon from '@/components/content/RemixIcon'
import SelectBoxApi from '@/components/layout/SelectBoxApi'
import Button from '@/components/content/Button'
import { UserClient } from '@/services/auth/login.types'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { callToGetMenuList } from '@/services/auth/apis'
import { MenuListRes } from '@/services/auth/menu.types'
import { Utils } from '@/utils/Utils'
import { Modal } from '@musinsa/mwms-ds'
import { CONSOLE_ENTRY_URL, CONSOLE_REQUIRED_AUTH_CD, hasConsoleAccess } from '@/utils/consoleEntry'

/**
 * 화주 전환 시 새로 만들 `AUTH_CD`.
 *
 * MEMO: AUTH_CD는 정책적으로 `화주코드 + 권한코드` 형태로 저장되기로 운영과 협의되어있음.
 * (MASTER 권한은 권한 코드 없이 화주코드 저장) 화주 변경시에 화주 별 AUTH_CD값도 변경되어야해서
 * fe에서 정책에 맞게 생성해서 요청주고 있음.
 *
 * LOGISPRD-12506 (PR 리뷰) — `MOMS_ENG`(개발자 온콜 조치 권한)도 `MASTER` 와 같은 **화주코드 접두사
 * 없는 단독값**이다. 예외에 넣지 않으면 화주 전환 한 번에 `M1PMOMS_ENG` 같은 값이 되어
 * `hasConsoleAccess`(정확히 'MOMS_ENG')가 막힌다. 세션의 auth 를 서버 값으로 되돌리는 경로는 로그인
 * 콜백뿐이라 **다시 로그인할 때까지 콘솔에 못 들어간다** — 새로고침으로는 복구되지 않는다.
 */
export const STANDALONE_AUTH_CODES = ['MASTER', CONSOLE_REQUIRED_AUTH_CD]

export const resolveConvertedAuthCd = (
  currentAuth: string | undefined,
  currentShipperCode: string | undefined,
  targetShipperCode: string,
): string => {
  const authLevel = STANDALONE_AUTH_CODES.includes(currentAuth ?? '')
    ? ''
    : currentShipperCode
      ? currentAuth?.replace(currentShipperCode, '')
      : currentAuth

  return authLevel ? targetShipperCode + authLevel : targetShipperCode
}

interface MainHeaderProps {
  /**
   * LOGISPRD-12506 — 헤더 안 "온콜 콘솔 ↗" 자기 링크 노출 여부.
   * 기본값 true(기존 어드민 동작과 동일). 콘솔 셸(GlobalHeader)은 이미 콘솔 화면 안이라
   * false 로 꺼서 자기 자신으로 가는 링크를 없앤다.
   *
   * LOGISPRD-12506 (검수 반영) — 이 prop 은 여전히 "on/off 스위치" 의미를 유지한다. 실제 렌더
   * 여부는 이 값과 권한 조건(`hasConsoleAccess`, AUTH_CD === 'MOMS_ENG')을 AND 로 합친
   * `canShowConsoleLink` 다 — 콘솔이 `showConsoleLink={false}` 로 끄는 경로(자기 자신에게는
   * 링크가 필요 없다)는 권한과 무관하게 그대로 동작한다.
   */
  showConsoleLink?: boolean
  /**
   * LOGISPRD-12506 — 화주 전환 버튼(+ 관련 다이얼로그) 노출 여부.
   * 기본값 true(기존 어드민 동작과 동일). 콘솔은 어드민 로그인 세션이 없어 화주 개념이 없으므로
   * false 로 꺼서 빈 버튼 껍데기가 남는 것을 막는다.
   */
  showShipperConvert?: boolean
}

function MainHeader({ showConsoleLink = true, showShipperConvert = true }: MainHeaderProps = {}) {
  const navigate = useNavigate()
  const userInfo = useMemo(() => AuthStorage.user.get(), [])

  // LOGISPRD-12506 (검수 반영) — 온콜 콘솔은 개발자 전용 화면이다. AUTH_CD 가 'MOMS_ENG' 인
  // 사용자에게만 자기 링크를 보여준다. showConsoleLink prop 의 기존 on/off 의미는 그대로 두고
  // 권한 조건을 AND 로 얹는다 — 콘솔 자신이 showConsoleLink={false} 로 끄는 경로는 이 조건과
  // 무관하게 계속 꺼져 있다.
  const canShowConsoleLink = showConsoleLink && hasConsoleAccess(userInfo.auth)

  // 화주 전환 기능 다이얼로그
  const [visibleConvert, setVisibleConvert] = useState(false)
  // 조회 중에는 버튼·닫기를 막는다 — 닫힌 뒤 성공하면 사용자가 모르는 사이 새로고침된다.
  const [converting, setConverting] = useState(false)
  // LOGISPRD-15454 — 대상 화주 메뉴를 먼저 조회하고 성공할 때만 세션을 바꾼다. 세션을 먼저 바꾸면 전환 불가 화주(403)에서
  // 새로고침 뒤에도 같은 403 이 반복된다. 요청 헤더에 세션 화주가 실리지 않아 대상 값으로 바로 조회된다.
  const onConvert = useCallback(async (clientInfo: UserClient) => {
    const { SHIPPER_CODE, SHIPPER_NAME } = clientInfo

    const newAuthCd = resolveConvertedAuthCd(userInfo.auth, userInfo.shipper_code, SHIPPER_CODE)

    setConverting(true)
    let menuList: MenuListRes
    try {
      menuList = await callToGetMenuList({ AUTH_CD: newAuthCd, SPR_NM: SHIPPER_CODE, email: userInfo.email ?? '' })
    } catch (e) {
      setConverting(false)
      const errorCode = Utils.getApiErrorCode(e)
      let message = '화주를 전환하지 못했어요. 다시 시도해 주세요.'
      if (errorCode === AUTH_ERROR_CODE.SHIPPER_NOT_CONVERTIBLE) {
        message = '이 화주로는 전환할 수 없어요. 권한을 확인해 주세요.'
      } else if (!errorCode && Utils.getApiStatus(e) === HTTP_BAD_REQUEST) {
        // errorCode 가 있는 400 은 2.0 의 요청 오류라 메뉴 0개가 아니다.
        message = SHIPPER_NO_MENU_MESSAGE
      }
      MAlert.show(message)
      return
    }
    // LOGISPRD-15703 — 2.0 은 메뉴 0개를 200 빈 배열로 준다. 그대로 전환하면 빈 사이드바만 남는다.
    if (menuList.length === 0) {
      setConverting(false)
      MAlert.show(SHIPPER_NO_MENU_MESSAGE)
      return
    }

    AuthStorage.user.set({
      ...userInfo,
      auth: newAuthCd,
      AUTH_CD: newAuthCd,
      company: SHIPPER_CODE,
      shipper_name: SHIPPER_NAME ?? SHIPPER_CODE,
      shipper_code: SHIPPER_CODE,
    })
    MenuStorage.menu.set(menuList)
    MenuStorage.menuOnSession.clear()
    navigate('/')
    setTimeout(() => window.location.reload(), 300)
  }, [navigate, userInfo])

  return (
    <HeaderWrapper>
      <HeaderLogo to="/">
        MOMS
        <span>Musinsa Order Management System</span>
      </HeaderLogo>
      <HeaderInfo>
        {/*
          LOGISPRD-12506 (어드민 편입) — 같은 탭 안에서의 클라이언트 사이드 이동이다.
          예전에는 별도 HTML(`/console.html`)을 `target="_blank"` 로 열었는데, 그 방식은 두 군데서
          깨졌다: (1) 배포 호스트 CDN 이 `/` 외 경로를 API 오리진으로 보내 정적 파일이 아니라 401
          JSON 이 떴고, (2) 세션 정보(`AuthStorage.user`)가 `sessionStorage` 라 새 탭에서는 비어
          있어 `MOMS_ENG` 권한자에게도 "권한이 없어요" 화면이 떴다(`rel="noreferrer"` 는 새 탭으로의
          sessionStorage 복제까지 막는다). 해시 이동은 둘 다 일으키지 않는다.
        */}
        {canShowConsoleLink && <HeaderConsoleLink to={CONSOLE_ENTRY_URL}>온콜 콘솔 ↗</HeaderConsoleLink>}
        <SelectBoxApi />
        {userInfo.nickname}
        {showShipperConvert && (
          <Button kind="primary_light" fit size="sm" onClick={() => setVisibleConvert(true)} disabled={!userInfo.is_convertible}>
            {userInfo.shipperName}
          </Button>
        )}
        {/* LOGISPRD-9129: 자물쇠 로그아웃 트리거.
            ※ AlertModal 은 onConfirm 을 await 하지 않으므로(`() => void` 로 fire-and-forget 호출) 이 await 는 redirect 를 게이팅하지 않는다.
              redirect 전 토큰 clear 는 oktaLogout 의 signOut({ clearTokensBeforeRedirect: true }) SDK 내부 순서(URL build → clear → assign)가 보장한다.
              여기 await + try/catch 의 실효는 (1) signOut reject 시 사용자 안내, (2) unhandled rejection 방지다(9057 인터셉터 경로는 가드가 별도 관찰).
            LLD anchor: docs/lld/auth/LLD-9129-okta-logout-cleanup.md "## 외부 경계 인터페이스" (3), "## 에러 정책". JIRA: LOGISPRD-9129 / Assignee: hyeongjin.na */}
        <RemixIcon
          name="ri-lock-line"
          onClick={() =>
            MConfirm.show('로그아웃 하시겠어요?', {
              onConfirm: async () => {
                try {
                  await AuthUtils.logout()
                } catch {
                  MAlert.show('로그아웃에 실패했어요. 다시 시도해 주세요.')
                }
              },
            })
          }
        />
      </HeaderInfo>
      <Modal open={visibleConvert} closable size="md" closeIcon title="화주사 변경" onClose={() => !converting && setVisibleConvert(false)}>
        <ClientListWrap>
          {userInfo.clientList?.map((client) => (
            <Button
              kind={userInfo.shipperCode === client.SHIPPER_CODE ? 'primary_light' : 'secondary'}
              fit
              size="lg"
              key={client.SHIPPER_CODE}
              disabled={converting}
              onClick={() => {
                onConvert(client).catch(() => undefined) // 실패 안내는 onConvert 안에서 한다
              }}
            >
              {client.SHIPPER_NAME}
            </Button>
          ))}
        </ClientListWrap>
      </Modal>
    </HeaderWrapper>
  )
}

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  height: ${HEADER_HEIGHT}px;
  background-color: #000;
  padding: 0 20px;
  color: #fff;
`

const HeaderLogo = styled(Link)`
  display: flex;
  padding-right: 20px;
  width: ${SIDEBAR_OPENED_WIDTH - 20}px;
  font-size: 24px;
  line-height: 24px;
  flex-direction: column;
  text-decoration: none;
  text-align: center;

  > span {
    font-size: 10px;
    line-height: 22px;
    text-decoration: none;
  }
`

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-grow: 1;
  gap: 18px;
  font-size: 14px;
  line-height: 14px;
`

const HeaderConsoleLink = styled(Link)`
  color: #fff;
  font-size: 14px;
  line-height: 14px;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`

const ClientListWrap = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  * {
    flex: 1 1 30%;
  }
`

export default MainHeader
